import {
  advanceInsuranceDerivedValue,
  EMPTY_INSURANCE_DERIVED_VALUE,
} from '@core/utils/insurance.utils';
import { createReducer, on } from '@ngrx/store';

import { SpecialtyColdChainState } from './interfaces/specialtyColdChain.interface';
import {
  _SetSpecialtyColdChainInsurance,
  ClearColdChainInternationalStep,
  SetColdChainShipmentIdentifiers,
  SetColdChainSpecialInstructions,
  SetSColdChainIsDomestic,
  SetSColdChainIsOriginDestinationValid,
  SetSpecialtyColdChainBilling,
  SetSpecialtyColdChainCommodity,
  SetSpecialtyColdChainCommodityValid,
  SetSpecialtyColdChainContentsValue,
  SetSpecialtyColdChainContentsValidity,
  ResetSpecialtyColdChainContents,
  SetSpecialtyColdChainScheduling,
  SetSpecialtyColdChainDestination,
  SetSpecialtyColdChainFormType,
  SetSpecialtyColdChainInsurance,
  SetSpecialtyColdChainInsuranceNeedHelp,
  SetSpecialtyColdChainInvoice,
  SetSpecialtyColdChainInvoiceNeedHelp,
  SetSpecialtyColdChainManualFormData,
  SetSpecialtyColdChainNotificationSettings,
  SetSpecialtyColdChainOrigin,
  SetSpecialtyColdChainToDefault,
  SetSpecialtyColdChaintoDefault,
  SetSpecialtyColdChainInsuranceMaxValueAndAutoDescription,
} from './specialty-cold-chain.actions';
import { SupplyDeliveryTimeEnum } from 'src/app/modules/shipping/shared/components/shipment-contents-and-supplies/interfaces-and-constants/shipment-contents-and-supplies.interface';

export const coldChainInitialState: SpecialtyColdChainState = {
  formType: {
    value: 'InteractiveForm',
  },
  shipment: {
    origin: {
      value: {},
      valid: false,
    },
    destination: {
      value: {},
      valid: false,
    },
    isOriginDestinationValid: { valid: false },
    scheduling: {
      value: {} as any,
      valid: false,
    },
    contents: {
      value: {
        packages: [],
        supplyDeliveryTime: SupplyDeliveryTimeEnum.WITH_PICKUP,
        shipmentDescription: '',
      },
      valid: false,
    },
    billing: {
      value: {},
      valid: false,
    },
    isDomestic: false,
  },
  international: {
    commercialInvoice: {
      value: {},
      valid: false,
      isNeedHelp: false,
      isUploadDocument: false,
      oldValue: {},
    },
    commodities: {
      value: [],
      valid: false,
    },
  },
  options: {
    insurance: {
      value: { insurance: false },
      needHelp: false,
      valid: true,
    },
    insuranceMaxValue: EMPTY_INSURANCE_DERIVED_VALUE,
    insuranceAutoDescription: EMPTY_INSURANCE_DERIVED_VALUE,
    _insurance: { value: {}, valid: true },
    shipmentIdentifier: { value: {}, valid: false },
    specialInstructions: { value: '', valid: true },
    notificationSettings: { value: [], valid: true },
  },
  manualForm: {
    files: [] as any[],
    count: 0,
    specialInstructions: '',
  },
};

export const specialtyColdChainReducer = createReducer(
  coldChainInitialState,
  on(SetSpecialtyColdChainOrigin, (state, { valid, value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        origin: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainDestination, (state, { valid, value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        destination: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainScheduling, (state, { valid, value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        scheduling: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainContentsValue, (state, { value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        contents: {
          ...state.shipment.contents,
          value,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainContentsValidity, (state, { valid }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        contents: {
          ...state.shipment.contents,
          valid,
        },
      },
    };
  }),
  on(ResetSpecialtyColdChainContents, (state) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        contents: {
          ...coldChainInitialState.shipment.contents,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainFormType, (state, { value }) => {
    return {
      ...state,
      formType: { value },
    };
  }),
  on(SetSpecialtyColdChainToDefault, (_) => {
    return {
      ...coldChainInitialState,
    };
  }),
  on(
    SetSpecialtyColdChainManualFormData,
    (state, { files, specialInstructions }) => {
      return {
        ...state,
        manualForm: {
          files,
          count: files.length,
          specialInstructions,
        },
      };
    }
  ),
  on(SetSpecialtyColdChaintoDefault, (state) => {
    return {
      ...state,
      manualForm: {
        ...coldChainInitialState.manualForm,
      },
    };
  }),
  on(SetSColdChainIsOriginDestinationValid, (state, { valid }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        isOriginDestinationValid: {
          valid,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainBilling, (state, { value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        billing: {
          value,
          valid: value?.siteId ? true : false,
        },
      },
    };
  }),
  on(_SetSpecialtyColdChainInsurance, (state, { valid, value }) => {
    return {
      ...state,
      options: {
        ...state.options,
        _insurance: {
          value: value,
          valid,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainInsurance, (state, { value, valid }) => {
    return {
      ...state,
      options: {
        ...state.options,
        insurance: {
          valid,
          value,
          needHelp: false,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainInsuranceNeedHelp, (state, { needHelp }) => {
    return {
      ...state,
      options: {
        ...state.options,
        insurance: {
          ...state.options.insurance,
          needHelp,
        },
      },
    };
  }),

  on(
    SetSpecialtyColdChainInvoice,
    (state, { valid, value, isUploadDocument, oldValue }) => {
      return {
        ...state,
        international: {
          ...state.international,
          commercialInvoice: {
            ...state.international.commercialInvoice,
            value,
            valid: state.international.commercialInvoice.isNeedHelp || valid,
            isUploadDocument,
            oldValue,
          },
        },
      };
    }
  ),
  on(SetSpecialtyColdChainInvoiceNeedHelp, (state, { isNeedHelp }) => {
    return {
      ...state,
      international: {
        ...state.international,
        commercialInvoice: {
          ...state.international.commercialInvoice,
          valid: isNeedHelp || state.international.commercialInvoice.valid,
          isNeedHelp,
        },
      },
    };
  }),
  on(SetColdChainSpecialInstructions, (state, { value }) => {
    return {
      ...state,
      options: {
        ...state.options,
        specialInstructions: {
          value,
          valid: true,
        },
      },
    };
  }),
  on(SetColdChainShipmentIdentifiers, (state, { value, valid }) => {
    return {
      ...state,
      options: {
        ...state.options,
        shipmentIdentifier: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainNotificationSettings, (state, { value, valid }) => {
    return {
      ...state,
      options: {
        ...state.options,
        notificationSettings: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetSColdChainIsDomestic, (state, { isDomestic }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        isDomestic: isDomestic,
      },
    };
  }),
  on(ClearColdChainInternationalStep, (state) => {
    return {
      ...state,
      international: {
        ...coldChainInitialState.international,
      },
    };
  }),
  on(SetSpecialtyColdChainCommodity, (state, { commodityList }) => {
    return {
      ...state,
      international: {
        ...state.international,
        commodities: {
          ...state.international.commodities,
          value: commodityList,
        },
      },
    };
  }),
  on(SetSpecialtyColdChainCommodityValid, (state, { valid }) => {
    return {
      ...state,
      international: {
        ...state.international,
        commodities: {
          ...state.international.commodities,
          valid,
        },
      },
    };
  }),
  on(
    SetSpecialtyColdChainInsuranceMaxValueAndAutoDescription,
    (state, { insuranceMaxValue, insuranceAutoDescription }) => {
      return {
        ...state,
        options: {
          ...state.options,
          insuranceMaxValue: advanceInsuranceDerivedValue(
            state.options.insuranceMaxValue,
            insuranceMaxValue
          ),
          insuranceAutoDescription: advanceInsuranceDerivedValue(
            state.options.insuranceAutoDescription,
            insuranceAutoDescription
          ),
        },
      };
    }
  )
);
